import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import Navigation from '../components/Navigation';
import KeyMetricsCards from '../components/wellness/KeyMetricsCards';
import HealthChartsSection from '../components/wellness/HealthChartsSection';
import HealthSummaryCards from '../components/wellness/HealthSummaryCards';
import HealthDataAccordion from '../components/wellness/HealthDataAccordion';
import AIJournalPromo from '../components/wellness/AIJournalPromo';
import WellnessRadarProfile from '../components/wellness/WellnessRadarProfile';
import WeeklyComparison from '../components/wellness/WeeklyComparison';
import StressPerformanceCorrelation from '../components/wellness/StressPerformanceCorrelation';
import DayOfWeekPatterns from '../components/wellness/DayOfWeekPatterns';
import PersonalRecords from '../components/wellness/PersonalRecords';
import RollingAverageTrend from '../components/wellness/RollingAverageTrend';
import WellnessGrowthTrends from '../components/wellness/WellnessGrowthTrends';
import { TrendingUp, TrendingDown, Target, Award, AlertCircle, Calendar, Users, Activity, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const WellnessTrends = () => {
  const { user } = useAuth();
  const [individualTrends, setIndividualTrends] = useState([]);
  const [aggregateTrends, setAggregateTrends] = useState([]);
  const [teamComparison, setTeamComparison] = useState([]);
  const [healthData, setHealthData] = useState([]);
  const [healthSummary, setHealthSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('individual'); // individual, aggregate, comparison
  const [timeRange, setTimeRange] = useState('30'); // 7, 30, 90 days

  useEffect(() => {
    fetchWellnessTrends();
  }, [timeRange]);

  const fetchWellnessTrends = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Fetch individual trends
      const individualResponse = await fetch(`${backendUrl}/api/wellness/trends/individual?days=${timeRange}`, { headers });
      
      if (individualResponse.ok) {
        const individualData = await individualResponse.json();
        setIndividualTrends(individualData);
      } else {
        // Fallback to mock data if API fails
        setIndividualTrends(generateMockIndividualTrends(parseInt(timeRange)));
      }

      // Fetch health data
      const healthResponse = await fetch(`${backendUrl}/api/health/data?days=${timeRange}`, { headers });
      if (healthResponse.ok) {
        const healthDataResult = await healthResponse.json();
        setHealthData(healthDataResult);
      }

      // Fetch health summary
      const summaryResponse = await fetch(`${backendUrl}/api/health/summary?days=${timeRange}`, { headers });
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setHealthSummary(summaryData);
      }

      // Fetch aggregate trends if user has permission
      if (user?.role && ['coach', 'team-admin', 'system-admin', 'sport-performance-consultant', 'school-admin'].includes(user.role)) {
        const aggregateResponse = await fetch(`${backendUrl}/api/wellness/trends/aggregate`, { headers });
        if (aggregateResponse.ok) {
          const aggregateData = await aggregateResponse.json();
          setAggregateTrends(aggregateData);
        } else {
          setAggregateTrends(generateMockAggregateTrends());
        }

        // Fetch sport comparison
        const comparisonResponse = await fetch(`${backendUrl}/api/wellness/trends/sport-comparison`, { headers });
        if (comparisonResponse.ok) {
          const comparisonData = await comparisonResponse.json();
          setTeamComparison(comparisonData);
        } else {
          setTeamComparison(generateMockTeamComparison());
        }
      }
    } catch (error) {
      console.error('Error fetching wellness trends:', error);
      // Fallback to mock data on error
      setIndividualTrends(generateMockIndividualTrends(parseInt(timeRange)));
      setAggregateTrends(generateMockAggregateTrends());
      setTeamComparison(generateMockTeamComparison());
    } finally {
      setLoading(false);
    }
  };

  const generateMockIndividualTrends = (days) => {
    const data = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        mood: Math.floor(Math.random() * 4) + 6, // 6-10
        stress: Math.floor(Math.random() * 3) + 3, // 3-6
        energy: Math.floor(Math.random() * 3) + 7, // 7-10
        focus: Math.floor(Math.random() * 3) + 6, // 6-9
        confidence: Math.floor(Math.random() * 3) + 7, // 7-10
        sleep_quality: Math.floor(Math.random() * 3) + 6, // 6-9
        performance_rating: Math.floor(Math.random() * 3) + 7 // 7-10
      });
    }
    return data;
  };

  const generateMockAggregateTrends = () => {
    return [
      { school: 'Central High School', avgMood: 7.8, avgStress: 4.2, avgPerformance: 8.1, athletes: 45 },
      { school: 'West Side High', avgMood: 7.5, avgStress: 4.8, avgPerformance: 7.9, athletes: 32 },
      { school: 'North Valley Academy', avgMood: 8.2, avgStress: 3.9, avgPerformance: 8.4, athletes: 28 },
      { school: 'East Ridge High', avgMood: 7.6, avgStress: 4.5, avgPerformance: 7.8, athletes: 51 },
      { school: 'South Mountain Prep', avgMood: 8.0, avgStress: 4.1, avgPerformance: 8.2, athletes: 38 }
    ];
  };

  const generateMockTeamComparison = () => {
    return [
      { sport: 'Basketball', avgMood: 8.1, avgStress: 4.0, avgPerformance: 8.3, athletes: 24 },
      { sport: 'Soccer', avgMood: 7.9, avgStress: 4.2, avgPerformance: 8.1, athletes: 32 },
      { sport: 'Swimming', avgMood: 8.3, avgStress: 3.8, avgPerformance: 8.5, athletes: 18 },
      { sport: 'Tennis', avgMood: 7.7, avgStress: 4.6, avgPerformance: 7.9, athletes: 16 },
      { sport: 'Track & Field', avgMood: 8.0, avgStress: 4.1, avgPerformance: 8.2, athletes: 28 },
      { sport: 'Volleyball', avgMood: 8.2, avgStress: 3.9, avgPerformance: 8.4, athletes: 20 }
    ];
  };

  const calculateTrend = (data, metric) => {
    if (data.length < 2) return 0;
    const recent = data.slice(-3).reduce((sum, item) => sum + item[metric], 0) / 3;
    const earlier = data.slice(0, 3).reduce((sum, item) => sum + item[metric], 0) / 3;
    return ((recent - earlier) / earlier * 100).toFixed(1);
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend < 0) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <div className="w-4 h-4 bg-gray-400 rounded-full"></div>;
  };

  const getTrendColor = (trend) => {
    if (trend > 5) return 'text-green-600';
    if (trend < -5) return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading wellness trends...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
      <Navigation user={user} onLogout={() => {}} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-12 text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent mb-2 sm:mb-4">
            Wellness Trends
          </h1>
          <p className="text-sm sm:text-lg text-gray-700 max-w-2xl mx-auto">
            <Activity className="inline h-4 w-4 sm:h-5 sm:w-5 text-emerald-500 mr-1 sm:mr-2" />
            Track wellness patterns over time
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-emerald-100">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* View Mode Selector */}
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('individual')}
                className={`px-4 py-2 rounded-lg font-medium transition-all shadow-sm ${
                  viewMode === 'individual'
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                    : 'bg-emerald-50 text-gray-700 hover:bg-emerald-100'
                }`}
              >
                Individual Trends
              </button>
              {user?.role && ['coach', 'team-admin', 'system-admin', 'sport-performance-consultant', 'school-admin'].includes(user.role) && (
                <>
                  <button
                    onClick={() => setViewMode('aggregate')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all shadow-sm ${
                      viewMode === 'aggregate'
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                        : 'bg-emerald-50 text-gray-700 hover:bg-emerald-100'
                    }`}
                  >
                    School Trends
                  </button>
                  <button
                    onClick={() => setViewMode('comparison')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all shadow-sm ${
                      viewMode === 'comparison'
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                        : 'bg-emerald-50 text-gray-700 hover:bg-emerald-100'
                    }`}
                  >
                    Sport Comparison
                  </button>
                </>
              )}
            </div>

            {/* Time Range Selector */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-emerald-600" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border-2 border-emerald-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Individual Trends View */}
        {viewMode === 'individual' && (
          <>
            {/* Personal Insights & Records - TOP */}
            <div className="mb-4">
              <PersonalRecords individualTrends={individualTrends} />
            </div>

            {/* Key Metrics Cards (condensed) */}
            <div className="mb-6">
              <KeyMetricsCards individualTrends={individualTrends} />
            </div>

            {/* Wellness Radar Profile + Growth Trends side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <WellnessRadarProfile individualTrends={individualTrends} />
              <WellnessGrowthTrends individualTrends={individualTrends} />
            </div>

            {/* Weekly Comparison */}
            <div className="mb-6">
              <WeeklyComparison individualTrends={individualTrends} />
            </div>

            {/* 7-Day Rolling Average Trends */}
            <div className="mb-6">
              <RollingAverageTrend individualTrends={individualTrends} />
            </div>

            {/* Stress-Performance Correlation + Day-of-Week Patterns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <StressPerformanceCorrelation individualTrends={individualTrends} />
              <DayOfWeekPatterns individualTrends={individualTrends} />
            </div>

            {/* Original Line Chart */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-emerald-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Daily Wellness Log</h2>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={individualTrends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }}
                    tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                  <Tooltip
                    labelFormatter={(d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="mood" stroke="#3B82F6" strokeWidth={2} name="Mood" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="energy" stroke="#10B981" strokeWidth={2} name="Energy" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="confidence" stroke="#8B5CF6" strokeWidth={2} name="Confidence" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="focus" stroke="#F59E0B" strokeWidth={2} name="Focus" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="stress" stroke="#EF4444" strokeWidth={2} name="Stress" dot={{ r: 2 }} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Health Charts Section - Extracted Component */}
            <HealthChartsSection healthData={healthData} />

            {/* AI Journal Promo - Extracted Component */}
            <AIJournalPromo />

            {/* Health Data Section - Extracted Components */}
            <HealthSummaryCards healthSummary={healthSummary} />
            <HealthDataAccordion healthData={healthData} />

          </>
        )}

        {/* Aggregate Trends View */}
        {viewMode === 'aggregate' && (
          <>
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">School Performance Comparison</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={aggregateTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="school" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="avgMood" fill="#3B82F6" name="Average Mood" />
                  <Bar dataKey="avgPerformance" fill="#10B981" name="Average Performance" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* School Stats */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">School Statistics</h3>
                <div className="space-y-4">
                  {aggregateTrends.map((school, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{school.school}</p>
                        <p className="text-sm text-gray-500">{school.athletes} athletes</p>
                      </div>
                      <div className="flex space-x-4 text-sm">
                        <div className="text-center">
                          <p className="text-blue-600 font-semibold">{school.avgMood}</p>
                          <p className="text-gray-500">Mood</p>
                        </div>
                        <div className="text-center">
                          <p className="text-green-600 font-semibold">{school.avgPerformance}</p>
                          <p className="text-gray-500">Performance</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stress Levels Distribution */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Stress Level Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={aggregateTrends}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ school, avgStress }) => `${school.split(' ')[0]}: ${avgStress}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="avgStress"
                    >
                      {aggregateTrends.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Team Comparison View */}
        {viewMode === 'comparison' && (
          <>
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sport Performance Comparison</h2>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={teamComparison}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sport" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="avgMood" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Mood" />
                  <Area type="monotone" dataKey="avgPerformance" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Performance" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamComparison.map((sport, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{sport.sport}</h3>
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">{sport.athletes}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Mood</span>
                      <span className="font-semibold text-blue-600">{sport.avgMood}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Stress</span>
                      <span className="font-semibold text-red-600">{sport.avgStress}/10</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Performance</span>
                      <span className="font-semibold text-green-600">{sport.avgPerformance}/10</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WellnessTrends;